import React from 'react';
import { Star } from 'lucide-react';

// BJCP Beer Styles - complete list
export const BJCP_STYLES = [
  "American Light Lager", "American Lager", "Cream Ale", "American Wheat Beer",
  "International Pale Lager", "International Amber Lager", "International Dark Lager",
  "Czech Pale Lager", "Czech Premium Pale Lager", "Czech Amber Lager", "Czech Dark Lager",
  "Munich Helles", "Festbier", "Helles Bock", "German Leichtbier", "Kölsch",
  "German Helles Exportbier", "German Pils", "Märzen", "Rauchbier", "Dunkles Bock",
  "Helles Bock", "Doppelbock", "Eisbock", "Weissbier", "Dunkles Weissbier",
  "Weizenbock", "Bavarian Kellerbier", "Pale Kellerbier", "British Golden Ale",
  "English IPA", "Extra Special Bitter", "Best Bitter", "Ordinary Bitter",
  "Dark Mild", "British Brown Ale", "English Porter", "Scottish Light",
  "Scottish Heavy", "Scottish Export", "Irish Stout", "Irish Extra Stout",
  "Sweet Stout", "Oatmeal Stout", "Tropical Stout", "Foreign Extra Stout",
  "British Strong Ale", "Old Ale", "Wee Heavy", "English Barleywine",
  "American Pale Ale", "American Amber Ale", "California Common", "American Brown Ale",
  "American Porter", "American Stout", "Imperial Stout", "American IPA",
  "Specialty IPA: Belgian IPA", "Specialty IPA: Black IPA", "Specialty IPA: Brown IPA",
  "Specialty IPA: Red IPA", "Specialty IPA: Rye IPA", "Specialty IPA: White IPA",
  "Specialty IPA: Brut IPA", "Double IPA", "Belgian Blond Ale", "Belgian Dubbel",
  "Belgian Tripel", "Belgian Golden Strong Ale", "Belgian Dark Strong Ale",
  "Trappist Single", "Belgian Pale Ale", "Bière de Garde", "Saison",
  "Belgian Witbier", "Lambic", "Gueuze", "Fruit Lambic", "Straight Sour Beer",
  "Flanders Red Ale", "Oud Bruin", "Gose", "Kentucky Common", "Lichtenhainer",
  "London Brown Ale", "Pre-Prohibition Lager", "Pre-Prohibition Porter",
  "Roggenbier", "Sahti", "Piwo Grodziskie", "Blonde Ale",
  "Fruit Beer", "Spice, Herb, or Vegetable Beer",
  "Autumn Seasonal Beer", "Winter Seasonal Beer", "Alternative Grain Beer",
  "Alternative Sugar Beer", "Classic Style Smoked Beer", "Specialty Smoked Beer",
  "Wood-Aged Beer", "Specialty Wood-Aged Beer", "Clone Beer", "Mixed-Style Beer",
  "Experimental Beer", "Historical Beer"
];

// Icon Components
export const KegIcon = ({ className = "w-8 h-8" }) => (
  <svg className={className} viewBox="0 0 48 48" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <ellipse cx="24" cy="10" rx="12" ry="3" />
    <ellipse cx="24" cy="38" rx="12" ry="3" />
    <line x1="12" y1="10" x2="12" y2="38" />
    <line x1="36" y1="10" x2="36" y2="38" />
    <ellipse cx="24" cy="16" rx="12" ry="2" opacity="0.5" />
    <ellipse cx="24" cy="24" rx="12" ry="2" opacity="0.5" />
    <ellipse cx="24" cy="32" rx="12" ry="2" opacity="0.5" />
    <rect x="22" y="6" width="4" height="4" rx="1" fill="currentColor" />
    <circle cx="24" cy="6" r="1" fill="white" />
  </svg>
);

export const TapIcon = ({ className = "w-5 h-5" }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 2v6" />
    <rect x="9" y="8" width="6" height="2" rx="1" fill="currentColor" />
    <path d="M8 10v2c0 1.1.9 2 2 2h4c1.1 0 2-.9 2-2v-2" />
    <path d="M11 14v8" />
    <path d="M13 14v8" />
    <path d="M10 22h4" />
  </svg>
);

export const MaintenanceIcon = ({ className = "w-5 h-5" }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" />
    <circle cx="18" cy="5" r="3" />
    <path d="M18 8v1" />
    <path d="M18 2v1" />
    <path d="M21 5h-1" />
    <path d="M16 5h-1" />
  </svg>
);

// Utility Functions
export const generateUUID = () => {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : ((r & 0x3) | 0x8);
    return v.toString(16);
  });
};

export const calculateFullWeight = (tare, volume, settings) => {
  const volumeInGallons = settings.volumeUnit === 'L' ? volume * 0.264172 : volume;
  const fullWeightLbs = tare + (volumeInGallons * settings.beerDensity);
  
  if (settings.weightUnit === 'kg') {
    return (fullWeightLbs * 0.453592).toFixed(1);
  }
  return fullWeightLbs.toFixed(1);
};

export const getMaintenanceStatus = (keg, item, settings) => {
  if (!keg.maintenanceRecords || keg.maintenanceRecords.length === 0) {
    return { status: 'overdue', message: 'No records', neverDone: true };
  }

  const relevantRecords = keg.maintenanceRecords
    .filter(record => record.items.includes(item))
    .sort((a, b) => new Date(b.date) - new Date(a.date));

  if (relevantRecords.length === 0) {
    return { status: 'overdue', message: 'Never done', neverDone: true };
  }

  const lastRecord = relevantRecords[0];
  const monthsSince = Math.floor((new Date() - new Date(lastRecord.date)) / (1000 * 60 * 60 * 24 * 30));

  const intervalConfig = settings.maintenanceIntervals[item];
  if (!intervalConfig) {
    return { status: 'ok', message: `${monthsSince} months ago`, neverDone: false };
  }

  if (!intervalConfig.months && !intervalConfig.batches) {
    return { status: 'ok', message: 'N/A', neverDone: false };
  }

  const monthsInterval = intervalConfig.months || null;
  const batchesInterval = intervalConfig.batches || null;

  // Check batches-based interval first (if applicable)
  const batchesSince = keg.batchCount || 0;
  if (batchesInterval) {
    if (batchesSince >= batchesInterval) {
      return { status: 'overdue', message: `${batchesSince} batches since last service`, neverDone: false };
    } else if (batchesSince >= Math.max(1, batchesInterval - 1)) {
      return { status: 'due-soon', message: `${batchesSince} batches since last service`, neverDone: false };
    }
  }

  // Then check time-based interval
  if (monthsInterval) {
    if (monthsSince >= monthsInterval) {
      return { status: 'overdue', message: `${monthsSince} months ago`, neverDone: false };
    } else if (monthsSince >= Math.max(0, monthsInterval - 2)) {
      return { status: 'due-soon', message: `${monthsSince} months ago`, neverDone: false };
    }
  }

  return { status: 'ok', message: `${monthsSince} months ago`, neverDone: false };
};

/**
 * Compute maintenance status for a location/tap line using tap_line_status and settings
 * Returns { status: 'overdue'|'due-soon'|'ok', message, batchCount, lastCleanedAt }
 */
export const getLocationMaintenanceStatus = (tapStatusRow, locationMaintenanceRecords, settings) => {
  const batchCount = (tapStatusRow && tapStatusRow.batch_count) ? tapStatusRow.batch_count : 0;
  const lastRecord = (locationMaintenanceRecords || []).filter(r => (
    (r.location === tapStatusRow.location) && ((r.sublocation === tapStatusRow.sublocation) || (r.sublocation == null && tapStatusRow.sublocation == null))
  )).sort((a, b) => new Date(b.maintenance_date) - new Date(a.maintenance_date))[0];

  const monthsSince = lastRecord ? Math.floor((new Date() - new Date(lastRecord.maintenance_date)) / (1000 * 60 * 60 * 24 * 30)) : null;
  const intervalConfig = settings?.maintenanceIntervals?.['Line Cleaning'] || {};
  const monthsInterval = intervalConfig.months || null;
  const batchesInterval = intervalConfig.batches || null;

  // Batches-based checks
  if (batchesInterval) {
    if (batchCount >= batchesInterval) return { status: 'overdue', message: `${batchCount} batches since last clean`, batchCount, lastCleanedAt: lastRecord?.maintenance_date };
    if (batchCount >= Math.max(1, batchesInterval - 1)) return { status: 'due-soon', message: `${batchCount} batches since last clean`, batchCount, lastCleanedAt: lastRecord?.maintenance_date };
  }

  // If there's no prior maintenance record, treat it as due so it shows up in the Maintenance Due section
  if (!lastRecord) {
    return { status: 'overdue', message: 'No record', batchCount, lastCleanedAt: null };
  }

  // Time-based checks (lastRecord exists, so monthsSince is defined)
  if (monthsInterval && monthsSince !== null) {
    if (monthsSince >= monthsInterval) return { status: 'overdue', message: `${monthsSince} months since last clean`, batchCount, lastCleanedAt: lastRecord?.maintenance_date };
    if (monthsSince >= Math.max(0, monthsInterval - 2)) return { status: 'due-soon', message: `${monthsSince} months since last clean`, batchCount, lastCleanedAt: lastRecord?.maintenance_date };
  }

  return { status: 'ok', message: `Last cleaned ${monthsSince} months ago`, batchCount, lastCleanedAt: lastRecord?.maintenance_date };
};

export const calculateAverageRating = (sessions) => {
  if (!sessions || sessions.length === 0) return 0;
  const allRatings = sessions.flatMap(s => s.ratings);
  if (allRatings.length === 0) return 0;
  const sum = allRatings.reduce((acc, r) => acc + r.rating, 0);
  return sum / allRatings.length;
};

export const getTotalRatings = (sessions) => {
  if (!sessions || sessions.length === 0) return 0;
  return sessions.reduce((total, s) => total + s.ratings.length, 0);
};

export const renderStars = (rating, interactive = false, onRate = null) => {
  const fullStars = Math.floor(rating);
  const remainder = rating - fullStars;
  const hasHalfStar = remainder >= 0.25 && remainder < 0.75;
  const hasThreeQuarterStar = remainder >= 0.75;
  
  return (
    <div className="flex gap-1 items-center">
      <div className="flex gap-0.5">
        {[1, 2, 3, 4, 5].map(i => {
          if (interactive) {
            return (
              <Star
                key={i}
                className={`w-6 h-6 cursor-pointer ${
                  i <= rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'
                }`}
                onClick={() => onRate && onRate(i)}
              />
            );
          }
          
          if (i <= fullStars) {
            return <Star key={i} className="w-5 h-5 fill-yellow-400 text-yellow-400" />;
          } else if (i === fullStars + 1 && hasHalfStar) {
            return (
              <div key={i} className="relative w-5 h-5">
                <Star className="w-5 h-5 text-gray-300 absolute" />
                <div className="overflow-hidden w-1/2 absolute">
                  <Star className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                </div>
              </div>
            );
          } else if (i === fullStars + 1 && hasThreeQuarterStar) {
            return (
              <div key={i} className="relative w-5 h-5">
                <Star className="w-5 h-5 text-gray-300 absolute" />
                <div className="overflow-hidden w-3/4 absolute">
                  <Star className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                </div>
              </div>
            );
          } else {
            return <Star key={i} className="w-5 h-5 text-gray-300" />;
          }
        })}
      </div>
      {!interactive && <span className="text-sm font-medium text-gray-700 ml-1">({rating.toFixed(1)})</span>}
    </div>
  );
};

// Constants
export const TAP_STATUSES = [
  { value: "Conditioning", days: true },
  { value: "Lagering", days: true },
  { value: "Carbonating", days: true },
  { value: "On Deck", days: false },
  { value: "On Tap", days: false },
  { value: "Empty", days: false }
];

export const CLEANING_STATUSES = ["Dirty", "Rinsed", "Clean", "Sanitized", "Purged"];
export const MAINTENANCE_ITEMS = ["Post O-Rings", "Poppet O-Rings", "Lid Gasket", "Tear Down", "Line Cleaning", "Water Filtration"];
export const USER_ROLES = ["Admin", "Editor", "Viewer"];
// ===== LOCATION UTILITIES =====
/**
 * Flattens location/sublocation structure into selectable options
 * If a location has sublocations, only sublocations are returned
 * If a location has no sublocations, the location itself is returned
 * @param {Array} locations - Array of location objects with optional sublocations
 * @returns {Array} Flattened array of {id, name, parentId} objects
 */
export const getSelectableLocations = (locations) => {
  if (!locations || locations.length === 0) return [];
  
  const options = [];
  
  locations.forEach(loc => {
    if (loc.sublocations && loc.sublocations.length > 0) {
      // If location has sublocations, only add sublocations
      loc.sublocations.forEach(sub => {
        options.push({
          id: sub.id,
          name: sub.name,
          parentId: loc.id,
          displayName: `${loc.name} → ${sub.name}`
        });
      });
    } else {
      // If location has no sublocations, add location itself
      options.push({
        id: loc.id,
        name: loc.name,
        parentId: null,
        displayName: loc.name
      });
    }
  });
  
  return options;
};

/**
 * Finds a location name from a stored value (name or sublocation name)
 * Used to display current selection in UI
 * @param {String} value - Stored location/sublocation name
 * @param {Array} locations - Array of location objects
 * @returns {String} Display name or original value
 */
export const getLocationDisplayName = (value, locations) => {
  if (!value || !locations) return value;
  
  for (const loc of locations) {
    if (loc.name === value) return loc.name;
    if (loc.sublocations) {
      const sub = loc.sublocations.find(s => s.name === value);
      if (sub) return `${loc.name} → ${sub.name}`;
    }
  }
  
  return value;
};

/**
 * Parses a location value into location and sublocation parts
 * If the value is a sublocation name, returns both location and sublocation
 * If the value is a location name with no sublocations, returns location and empty sublocation
 * @param {String} value - Location or sublocation name
 * @param {Array} locations - Array of location objects
 * @returns {Object} {location: string, sublocation: string}
 */
export const parseLocationValue = (value, locations) => {
  if (!value || !locations) return { location: value || '', sublocation: '' };
  
  for (const loc of locations) {
    // Check if it's a sublocation
    if (loc.sublocations) {
      const sub = loc.sublocations.find(s => s.name === value);
      if (sub) return { location: loc.name, sublocation: sub.name };
    }
    // Check if it's a location
    if (loc.name === value) return { location: loc.name, sublocation: '' };
  }
  
  return { location: value, sublocation: '' };
};

/**
 * Compute maintenance status for brewery-level items (e.g., Water Filtration)
 * @param {Array} records - maintenance records for this brewery filtered by item
 * @param {String} item - item name (e.g., 'Water Filtration')
 * @param {Object} settings - app settings (contains maintenanceIntervals)
 * @returns {{status: 'overdue'|'due-soon'|'ok', message: string, neverDone: boolean}}
 */
export const getBreweryMaintenanceStatus = (records, item, settings) => {
  const intervalConfig = settings?.maintenanceIntervals?.[item];

  if (!records || records.length === 0) {
    // If no interval configured, consider it N/A (OK), otherwise treat as never done (overdue)
    if (!intervalConfig || (!intervalConfig.months && !intervalConfig.batches)) {
      return { status: 'ok', message: 'No schedule', neverDone: true };
    }
    return { status: 'overdue', message: 'Never serviced', neverDone: true };
  }

  const lastRecord = records.sort((a, b) => new Date(b.maintenance_date) - new Date(a.maintenance_date))[0];
  const monthsSince = lastRecord ? Math.floor((new Date() - new Date(lastRecord.maintenance_date)) / (1000 * 60 * 60 * 24 * 30)) : null;

  if (!intervalConfig || (!intervalConfig.months && !intervalConfig.batches)) {
    return { status: 'ok', message: `${monthsSince ?? '0'} months ago`, neverDone: false };
  }

  const monthsInterval = intervalConfig.months || null;

  if (monthsInterval) {
    if (monthsSince >= monthsInterval) return { status: 'overdue', message: `${monthsSince} months ago`, neverDone: false };
    if (monthsSince >= Math.max(0, monthsInterval - 2)) return { status: 'due-soon', message: `${monthsSince} months ago`, neverDone: false };
  }

  return { status: 'ok', message: `${monthsSince} months ago`, neverDone: false };
};